import { Fragment, useState, useEffect, useRef, useLayoutEffect } from 'react'
import { FiRefreshCw, FiTruck, FiShoppingCart, FiDroplet, FiDatabase, FiUsers, FiInfo, FiAlertCircle, FiAlertTriangle, FiCheckCircle, FiChevronDown, FiChevronRight, FiZap, FiTarget, FiDownload, FiShare2 } from 'react-icons/fi'
import api from '../utils/api'
import useIsMobile from '../utils/useIsMobile'
import LojDetayModal from '../components/LojDetayModal'

// Hücre tıklanınca hangi detay modalı açılsın? (lojistik cari/sefer/araç)
const _detayTetik = (key, val, row) => {
  const s = (val == null) ? '' : String(val).trim()
  if (!s || s === '-' || s === '—') return null
  if (key === 'grup' && row && row._grup_ds) return { type: 'grup', value: s, extra: { ds_id: row._grup_ds } }
  if (key === 'plaka') return { type: 'arac', value: s }
  // Alt satır (sefer_detay taşıması): sefer_no hücresine basınca sefere değil sefer_detay'a git
  if ((key === 'sefer_no' || key === 'id') && row && row._subrow) {
    return row._detay_id ? { type: 'sefer_detay', value: String(row._detay_id) } : null
  }
  if (key === 'sefer_no' || key === 'id') return /^\d+$/.test(s) ? { type: 'sefer', value: s } : null
  if (key === 'musteri' || key === 'gonderici' || key === 'alici') {
    const unvan = s.replace(/\s*\((Alıcı|Gönderici)\)\s*$/, '').trim()
    if (!unvan) return null
    const base = { type: row && row._petrol_cari ? 'petrol-cari' : 'cari', value: unvan }
    // Sipariş Düşen Müşteriler: bakiye açılır ama içinde "Seferler" butonu olsun (faturalanacak cari)
    if (key === 'musteri' && row && row._seferler_cari) base.extra = { seferler_cari: row._seferler_cari }
    return base
  }
  return null
}

const FragmentRow = ({ children }) => <Fragment>{children}</Fragment>

async function exportRows(rows, columns, filename, title, format, mode = 'download') {
  try {
    // Sadece kolon-key'lerini al, label adı ile data hazırla; _ ile başlayan internal field'ları atla
    const cleanCols = (columns || []).filter(c => !c.key.startsWith('_'))
    const cleanRows = rows.map(r => {
      const o = {}
      cleanCols.forEach(c => {
        let v = r[c.key]
        if (v == null) v = ''
        else if (typeof v === 'object') v = String(v)
        o[c.label || c.key] = v
      })
      return o
    })
    const token = localStorage.getItem('token')
    const resp = await fetch(`/api/export/${format}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ data: cleanRows, filename, title })
    })
    if (!resp.ok) return
    const blob = await resp.blob()
    const ext = format === 'xlsx' ? 'xlsx' : 'pdf'
    const mime = format === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf'
    const fname = `${filename}.${ext}`
    // Paylas modu - Web Share API (mobil: WhatsApp dahil)
    if (mode === 'share' && navigator.canShare) {
      const file = new File([blob], fname, { type: mime })
      if (navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], title, text: title }); return } catch (e) { /* iptal -> indir fallback */ }
      }
    }
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fname
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) { console.error('Export hatasi:', e) }
}

function ExportButtons({ rows, columns, filename, title, label, isMobile }) {
  if (!rows || rows.length === 0) return null
  // Mobil: paylas modu (WhatsApp vs.), Masaustu: indir
  const mode = isMobile ? 'share' : 'download'
  const Icon = isMobile ? FiShare2 : FiDownload
  return (
    <div className="flex items-center gap-1">
      <button onClick={(e) => { e.stopPropagation(); exportRows(rows, columns, filename, title, 'xlsx', mode) }}
        className="text-[10px] bg-green-500/15 hover:bg-green-500/30 text-green-700 dark:text-green-400 rounded px-1.5 py-0.5 flex items-center gap-1"
        title={`${label} - Excel ${isMobile ? 'paylas' : 'indir'}`}>
        <Icon size={10}/> XLSX
      </button>
      <button onClick={(e) => { e.stopPropagation(); exportRows(rows, columns, filename, title, 'pdf', mode) }}
        className="text-[10px] bg-red-500/15 hover:bg-red-500/30 text-red-700 dark:text-red-400 rounded px-1.5 py-0.5 flex items-center gap-1"
        title={`${label} - PDF ${isMobile ? 'paylas' : 'indir'}`}>
        <Icon size={10}/> PDF
      </button>
    </div>
  )
}

function exportSection(sec) {
  // Tum tablolari tek dosyada degil, ilk tabloyu disari aktar — basit yaklaşım
  // Section icin: ilk tablo + KPI'lari ekleme
  if (sec._render === 'yorum') {
    // Akilli Yorum: uyarilari export et
    const rows = (sec.uyarilar || []).map(u => ({
      Tip: u.tip, Kategori: u.kategori, Mesaj: u.mesaj, Detay: u.detay || ''
    }))
    return { rows, columns: [
      { key: 'Tip', label: 'Tip' }, { key: 'Kategori', label: 'Kategori' },
      { key: 'Mesaj', label: 'Mesaj' }, { key: 'Detay', label: 'Detay' }
    ]}
  }
  // Standart section: butun tablolari birlestir
  const allRows = []
  for (const tbl of (sec.tables || [])) {
    for (const r of (tbl.rows || [])) {
      allRows.push({ _Tablo: tbl.title, ...r })
    }
  }
  // Kolonlari topla
  const colMap = {}
  ;(sec.tables || []).forEach(tbl => (tbl.columns || []).forEach(c => { colMap[c.key] = c }))
  const cols = [{ key: '_Tablo', label: 'Tablo' }, ...Object.values(colMap)]
  return { rows: allRows, columns: cols }
}

const SEFER_DETAY_COLS = [
  { key: 'irsaliye_no', label: 'İrsaliye' },
  { key: 'yukleme_tarihi', label: 'Yükleme' },
  { key: 'bosaltim_tarihi', label: 'Boşaltma' },
  { key: 'yukleme_yeri', label: 'Yükleme Yeri' },
  { key: 'bosaltim_yeri', label: 'Boşaltma Yeri' },
  { key: 'gonderen', label: 'Gönderen' },
  { key: 'alici', label: 'Alıcı' },
  { key: 'kg', label: 'KG', format: 'num' },
  { key: 'lt', label: 'LT', format: 'num' },
  { key: 'birim_fiyat', label: 'Birim Fiyat', format: 'num' },
  { key: 'tutar', label: 'Tutar', format: 'tl' },
  { key: 'kdv', label: 'KDV%', format: 'int' },
  { key: 'fatura', label: 'Fatura' },
]

const fmtNum = (v) => v == null ? '-' : Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const YAKIT_RENKLERI = {
  'MOTORİN': '#b45309',
  'BENZİN': '#16a34a',
  'LPG': '#2563eb',
}
const yakitRengi = (y) => YAKIT_RENKLERI[y] || '#6b7280'

function TankBar({ seviye, bos, kapasite, doluluk, alt1, alt2 }) {
  const dol = Math.max(0, Math.min(100, doluluk || 0))
  const low = (doluluk || 0) < 20
  const mid = !low && (doluluk || 0) < 40
  const barColor = low ? '#dc2626' : (mid ? '#f59e0b' : '#2563eb')
  const txtColor = low ? 'text-red-600 dark:text-red-400' : (mid ? 'text-amber-600 dark:text-amber-400' : 'text-blue-700 dark:text-blue-400')
  return (
    <div className="flex flex-col items-center flex-shrink-0" style={{ width: 60 }}>
      <div className="text-[9px] text-gray-500 dark:text-gray-400 tabular-nums mb-0.5">{formatLt(kapasite)}</div>
      <div className="relative w-11 rounded overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col justify-end"
           style={{ height: 130, background: '#e5e7eb' }}>
        <div className="absolute top-0 left-0 right-0 text-center text-[8px] text-gray-500 pt-0.5 tabular-nums">{formatLt(bos)}</div>
        <div className="w-full flex items-end justify-center" style={{ height: `${dol}%`, background: barColor }}>
          <span className="text-[8px] text-white pb-0.5 tabular-nums leading-none">{formatLt(seviye)}</span>
        </div>
      </div>
      <div className={`text-[11px] font-bold tabular-nums mt-0.5 ${txtColor}`}>%{(doluluk || 0).toFixed(0)}</div>
      <div className="text-[8px] text-gray-600 dark:text-gray-400 text-center leading-tight">{alt1}{alt2 ? <><br/>{alt2}</> : null}</div>
    </div>
  )
}

function TankCharts({ section }) {
  // Her istasyon: SOL=tüm tanklar (tek tek), ORTA=yakıt bazlı toplam çubuk (tablonun grafiği), SAĞ=tablo
  const tankBars = section._render_extras?.tank_bars || {}
  const istasyonlar = Object.keys(tankBars).filter(i => i !== 'Toplam')
  if (!istasyonlar.length) return null
  const tblMap = {}
  ;(section.tables || []).forEach(t => { tblMap[t.title] = t })
  return (
    <div className="space-y-4">
      {istasyonlar.map(ist => {
        const tanklar = tankBars[ist] || []
        const tbl = tblMap[ist]
        // Yakıt bazlı toplam (tablodan, TOPLAM satırı hariç) → orta sütun çubukları
        const yakitToplam = (tbl?.rows || []).filter(r => !r._total).map(r => ({
          ...r, doluluk: r.kapasite > 0 ? (r.seviye / r.kapasite * 100) : 0,
        }))
        return (
          <div key={ist} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-100 dark:border-gray-700">
            <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-400 mb-3">{ist}</h4>
            <div className="flex flex-col lg:flex-row gap-4">
              {/* SOL: her tank ayrı çubuk */}
              <div className="flex items-end gap-3 overflow-x-auto pb-1 flex-1">
                {tanklar.map((t, i) => (
                  <TankBar key={i} seviye={t.seviye} bos={t.bos} kapasite={t.kapasite} doluluk={t.doluluk} alt1={t.tank} alt2={t.yakit} />
                ))}
              </div>
              {/* ORTA: yakıt bazlı toplam çubuk (5 Motorin tankı = tek Motorin çubuğu) */}
              <div className="flex items-end gap-3 overflow-x-auto pb-1 lg:border-l lg:border-dashed lg:border-gray-300 lg:dark:border-gray-600 lg:pl-3 flex-shrink-0">
                {yakitToplam.map((r, i) => (
                  <TankBar key={i} seviye={r.seviye} bos={r.bos} kapasite={r.kapasite} doluluk={r.doluluk} alt1={r.yakit} />
                ))}
              </div>
              {/* SAĞ: yakıt bazlı toplam tablosu */}
              {tbl && (
                <div className="lg:w-2/5 flex-shrink-0">
                  <div className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-1">{ist}</div>
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr>{(tbl.columns || []).map((c, ci) => (
                        <th key={ci} className="border border-gray-200 dark:border-gray-700 px-2 py-1 bg-blue-50 dark:bg-gray-800 text-left text-gray-600 dark:text-gray-300">{c.label}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {(tbl.rows || []).map((r, ri) => (
                        <tr key={ri} className={r._total ? 'font-bold bg-blue-50/50 dark:bg-gray-800/50' : ''}>
                          {(tbl.columns || []).map((c, ci) => (
                            <td key={ci} className={`border border-gray-200 dark:border-gray-700 px-2 py-1 ${c.format ? 'text-right tabular-nums' : ''} text-gray-700 dark:text-gray-200`}>
                              {formatCell(r[c.key], c.format, c.currency)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const AY_ADLARI = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık']

function AyPicker({ year, month, onChange }) {
  const now = new Date()
  // Son 24 ay
  const options = []
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    options.push({ y: d.getFullYear(), m: d.getMonth() + 1 })
  }
  const change = (val) => {
    const [y, m] = val.split('-').map(Number)
    onChange(y, m)
  }
  const value = `${year}-${month}`
  const goPrev = () => {
    const d = new Date(year, month - 2, 1)
    onChange(d.getFullYear(), d.getMonth() + 1)
  }
  const goNext = () => {
    const d = new Date(year, month, 1)
    const todayYM = now.getFullYear() * 12 + now.getMonth()
    const nxtYM = d.getFullYear() * 12 + d.getMonth()
    if (nxtYM > todayYM) return  // ileri gitme
    onChange(d.getFullYear(), d.getMonth() + 1)
  }
  return (
    <div className="flex items-center gap-1 ml-2">
      <button onClick={goPrev} className="text-xs p-1 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">◀</button>
      <select value={value} onChange={e => change(e.target.value)}
        className="text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-gray-700 dark:text-gray-200 cursor-pointer">
        {options.map(o => (
          <option key={`${o.y}-${o.m}`} value={`${o.y}-${o.m}`}>{AY_ADLARI[o.m-1]} {o.y}</option>
        ))}
      </select>
      <button onClick={goNext} className="text-xs p-1 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">▶</button>
    </div>
  )
}

const UYARI_STYLES = {
  danger:  { bg: 'bg-red-50 dark:bg-red-900/20',    border: 'border-red-400 dark:border-red-500',    text: 'text-red-800 dark:text-red-200',    icon: FiAlertCircle,  iconCls: 'text-red-600 dark:text-red-400' },
  warning: { bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-400 dark:border-amber-500', text: 'text-amber-800 dark:text-amber-200', icon: FiAlertTriangle, iconCls: 'text-amber-600 dark:text-amber-400' },
  oneri:   { bg: 'bg-indigo-50 dark:bg-indigo-900/20', border: 'border-indigo-400 dark:border-indigo-500', text: 'text-indigo-800 dark:text-indigo-200', icon: FiTarget,       iconCls: 'text-indigo-600 dark:text-indigo-400' },
  success: { bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-400 dark:border-green-500', text: 'text-green-800 dark:text-green-200', icon: FiCheckCircle,  iconCls: 'text-green-600 dark:text-green-400' },
  info:    { bg: 'bg-blue-50 dark:bg-blue-900/20',   border: 'border-blue-400 dark:border-blue-500',   text: 'text-blue-800 dark:text-blue-200',   icon: FiInfo,         iconCls: 'text-blue-600 dark:text-blue-400' },
}

function YorumKartlari({ uyarilar }) {
  const [filter, setFilter] = useState('all')
  const sayilar = uyarilar.reduce((a, u) => { a[u.tip] = (a[u.tip] || 0) + 1; return a }, {})
  const filtered = filter === 'all' ? uyarilar : uyarilar.filter(u => u.tip === filter)
  return (
    <div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-xs text-gray-500 dark:text-gray-400">Filtre:</span>
        {[
          { k: 'all', l: `Tümü (${uyarilar.length})`, cls: '' },
          { k: 'danger', l: `Kritik (${sayilar.danger || 0})`, cls: 'text-red-700 dark:text-red-300' },
          { k: 'warning', l: `Uyarı (${sayilar.warning || 0})`, cls: 'text-amber-700 dark:text-amber-300' },
          { k: 'oneri', l: `Öneri (${sayilar.oneri || 0})`, cls: 'text-indigo-700 dark:text-indigo-300' },
          { k: 'info', l: `Bilgi (${sayilar.info || 0})`, cls: 'text-blue-700 dark:text-blue-300' },
          { k: 'success', l: `Başarı (${sayilar.success || 0})`, cls: 'text-green-700 dark:text-green-300' },
        ].map(o => (
          <button key={o.k} onClick={() => setFilter(o.k)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filter === o.k ? 'bg-primary-600 text-white border-primary-600' : `bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 ${o.cls}`}`}>
            {o.l}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {filtered.length === 0 && <p className="text-xs text-gray-400 italic">Bu filtre için uyarı yok.</p>}
        {filtered.map((u, i) => {
          const s = UYARI_STYLES[u.tip] || UYARI_STYLES.info
          const IconEl = s.icon
          return (
            <div key={i} className={`flex gap-3 p-3 rounded-lg border-l-4 ${s.bg} ${s.border}`}>
              <IconEl className={`${s.iconCls} mt-0.5 flex-shrink-0`} size={18}/>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className={`text-[10px] font-bold uppercase tracking-wide ${s.iconCls}`}>{u.kategori}</span>
                </div>
                <p className={`text-sm font-medium ${s.text}`}>{u.mesaj}</p>
                {u.detay && <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{u.detay}</p>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function DetayTable({ details, columns, title }) {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded overflow-auto max-h-[50vh] bg-white dark:bg-gray-800">
      <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase px-2 py-1 bg-gray-100 dark:bg-gray-900/60 border-b dark:border-gray-700 sticky top-0 z-30">
        {title || 'Detay'} ({details.length})
      </div>
      <table className="w-full text-[11px] anlik-table">
        <thead className="sticky top-6 z-20">
          <tr className="bg-gray-50 dark:bg-gray-700/50">
            {columns.map((c, ci) => (
              <th key={c.key} className={`px-2 py-1 text-left font-medium text-gray-600 dark:text-gray-300 ${c.format ? 'text-right' : ''} bg-gray-50 dark:bg-gray-700/95 ${ci === 0 ? 'sticky left-0 z-30 shadow-[2px_0_3px_-1px_rgba(0,0,0,0.06)]' : ''}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {details.map((d, i) => (
            <tr key={i} className="border-t border-gray-100 dark:border-gray-700 hover:bg-blue-50/30 dark:hover:bg-blue-900/10">
              {columns.map((c, ci) => {
                let v = d[c.key]
                if (c.format === 'num') v = fmtNum(v)
                else if (c.format === 'tl') v = v ? Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺' : '-'
                else if (c.format === 'int') v = v ?? '-'
                else v = v || '-'
                return (
                  <td key={c.key} className={`px-2 py-1 text-gray-700 dark:text-gray-300 ${c.format ? 'text-right tabular-nums' : ''} ${ci === 0 ? 'sticky left-0 z-[1] bg-white dark:bg-gray-800 shadow-[2px_0_3px_-1px_rgba(0,0,0,0.06)]' : ''}`}>
                    {v}
                  </td>
                )
              })}
            </tr>
          ))}
          {details.some(d => d.aciklama) && (
            <tr>
              <td colSpan={columns.length} className="px-2 py-1 text-[10px] italic text-gray-500 dark:text-gray-400">
                {details.filter(d => d.aciklama).map(d => `#${d.id}: ${d.aciklama}`).join(' • ')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

const formatMoney = (v, cur = 'TL') => {
  if (v == null) return ''
  return Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + cur
}
const formatLt = (v) => Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Lt'
const formatInt = (v) => Number(v || 0).toLocaleString('tr-TR')
const formatCell = (val, fmt, currency) => {
  if (val == null) return '-'
  if (fmt === 'tl') return formatMoney(val, 'TL')
  if (fmt === 'money') return formatMoney(val, currency || 'TL')
  if (fmt === 'lt') return formatLt(val)
  if (fmt === 'int') return formatInt(val)
  if (fmt === 'num') return fmtNum(val)
  if (fmt === 'pct') return Number(val).toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %'
  if (fmt === 'delta') {
    return <CompareGauge percent={val} />
  }
  return String(val)
}

function CompareGauge({ percent }) {
  if (percent === null || percent === undefined || isNaN(Number(percent))) {
    return <span className="text-gray-400">—</span>
  }
  const raw = Number(percent)
  const p = Math.max(-100, Math.min(100, raw))
  // Yarim daire: -100 = sol uc (180°), 0 = ust (90°), +100 = sag uc (0°)
  // SVG'de 0° saat 3 yonu. Bizim koordinat: angle (0-180) gauge derecesi, 0=sol, 90=ust, 180=sag
  const gaugeDeg = 90 + (p / 100) * 90  // 0..180
  const cx = 50, cy = 42, r = 32
  const rad = (180 - gaugeDeg) * Math.PI / 180
  const tipX = cx + r * Math.cos(rad)
  const tipY = cy - r * Math.sin(rad)
  // Renk skalasi - kotuden iyiye
  const color = p <= -50 ? '#dc2626' : p <= -20 ? '#ef4444' : p < 0 ? '#f59e0b' : p < 5 ? '#84cc16' : p < 30 ? '#22c55e' : '#10b981'
  const arrow = p > 0 ? '▲' : p < 0 ? '▼' : '●'
  const sign = p > 0 ? '+' : ''
  // %100 asiminda 'MAX' isareti
  const isMax = Math.abs(raw) >= 100
  const txt = isMax ? `${raw > 0 ? '+' : '-'}%${Math.round(Math.abs(raw))}` : `${arrow} ${sign}${p.toFixed(1)}%`
  // Renkli yarim daire 2 yariya bolelim - sol kirmizi-sari, sag yesil
  return (
    <div className="inline-flex flex-col items-center" title={`${raw.toFixed(2)}% (onceki donem)`}>
      <svg viewBox="0 0 100 55" width="62" height="34">
        <defs>
          <linearGradient id="leftArc" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#dc2626" />
            <stop offset="100%" stopColor="#fbbf24" />
          </linearGradient>
          <linearGradient id="rightArc" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#86efac" />
            <stop offset="100%" stopColor="#16a34a" />
          </linearGradient>
        </defs>
        {/* Sol yari arc (kotu) */}
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx} ${cy - r}`}
              fill="none" stroke="url(#leftArc)" strokeWidth="7" strokeLinecap="butt" />
        {/* Sag yari arc (iyi) */}
        <path d={`M ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
              fill="none" stroke="url(#rightArc)" strokeWidth="7" strokeLinecap="butt" />
        {/* Orta cizgi (0%) */}
        <line x1={cx} y1={cy - r - 2} x2={cx} y2={cy - r + 4} stroke="#94a3b8" strokeWidth="1" />
        {/* Ibre */}
        <line x1={cx} y1={cy} x2={tipX} y2={tipY} stroke="#1f2937" strokeWidth="2.5" strokeLinecap="round" />
        {/* Merkez nokta */}
        <circle cx={cx} cy={cy} r="3" fill="#1f2937" />
      </svg>
      <span className="text-[10px] font-semibold leading-none mt-0.5" style={{ color }}>
        {txt}
      </span>
    </div>
  )
}


const ICONS = { truck: FiTruck, 'shopping-cart': FiShoppingCart, droplet: FiDroplet, database: FiDatabase, users: FiUsers, lightbulb: FiZap }

const getSecId = (title) => {
  if (!title) return null
  if (title.startsWith('Lojistik')) return 'loj'
  if (title.startsWith('Araç')) return 'arac'
  if (title.startsWith('Market (Pocan Kumpir')) return 'market-kumpir'
  if (title.startsWith('Market')) return 'market'
  if (title.startsWith('Petrol (Tatlıcak')) return 'petrol-tat'
  if (title.startsWith('Petrol (Ladık')) return 'petrol-lad'
  if (title.startsWith('Petrol')) return 'petrol'
  if (title.startsWith('Tank')) return 'tank'
  if (title.startsWith('Müşteri Sıklığı (Petrol Tatlıcak')) return 'kohort-tat'
  if (title.startsWith('Müşteri Sıklığı (Petrol Ladık')) return 'kohort-lad'
  if (title.startsWith('Müşteri')) return 'kohort'
  return null
}

export default function AnlikRapor() {
  const isMobile = useIsMobile()
  const [period, setPeriod] = useState('daily')
  // qr22: para modu panolardakiyle AYNI anahtarda saklanir — bir yerde ₺ secince her yerde ₺
  const _kmUser = JSON.parse(localStorage.getItem('user') || '{}')?.id || 'anon'
  const [paraModu, setParaModuState] = useState(
    () => localStorage.getItem(`currencyMode_user_${_kmUser}`) || localStorage.getItem('currencyMode') || '')
  const setParaModu = (v) => {
    localStorage.setItem(`currencyMode_user_${_kmUser}`, v)
    localStorage.setItem('currencyMode', v)
    setParaModuState(v)
  }
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const today = new Date()
  const [aracYear, setAracYear] = useState(today.getFullYear())
  const [aracMonth, setAracMonth] = useState(today.getMonth() + 1)
  // Dönem değişince araç ayını da dönemin referans ayına ayarla: "Dün" -> dünün ayı
  // (ayın 1'inde "Dün" = önceki ay). Kullanıcı ay seçiciden yine istediği ayı seçebilir.
  useEffect(() => {
    const n = new Date()
    const ref = period === 'yesterday'
      ? new Date(n.getFullYear(), n.getMonth(), n.getDate() - 1)
      : n
    setAracYear(ref.getFullYear())
    setAracMonth(ref.getMonth() + 1)
  }, [period])
  const [collapsed, setCollapsed] = useState({})
  const [collapsedSecs, setCollapsedSecs] = useState({})
  const [expandedRows, setExpandedRows] = useState({})
  const [openInfo, setOpenInfo] = useState({})
  const [refreshing, setRefreshing] = useState(false)  // yenile tusu: arka plan hesaplama suruyor
  const [detayModal, setDetayModal] = useState(null)   // {type,value} cari/sefer/arac pop-up
  const [viewMode, setViewMode] = useState('mail')     // 'mail' = mail tasarımı birebir, 'klasik' = React tablolar
  const [htmlContent, setHtmlContent] = useState('')
  const [htmlLoading, setHtmlLoading] = useState(false)
  const [raporOpen, setRaporOpen] = useState(false)    // Rapor Al modalı
  const [selRapor, setSelRapor] = useState(new Set())  // seçili "section|||table"
  const [raporBusy, setRaporBusy] = useState(false)

  // Blob indirme axios interceptor'ı ile bozuluyor → fetch kullan (CariDetayPanel export ile aynı)
  const _raporFetch = async (format) => {
    const tables = Array.from(selRapor).map(k => { const [s, t] = k.split('|||'); return { section: s, table: t } })
    if (!tables.length) return null
    const token = localStorage.getItem('token')
    const resp = await fetch('/api/anlik-rapor/export', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ period, format, tables, para: paraModu }),
    })
    if (!resp.ok) throw new Error('Sunucu ' + resp.status)
    return await resp.blob()
  }

  const raporIndir = async (format) => {
    if (!selRapor.size) return
    setRaporBusy(true)
    try {
      const blob = await _raporFetch(format)
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `anlik_rapor_${data?.start_date || ''}.${format}`; a.click()
      setTimeout(() => URL.revokeObjectURL(url), 4000)
    } catch (e) { alert('Rapor indirilemedi: ' + (e?.message || '')) }
    finally { setRaporBusy(false) }
  }

  const raporYazdir = async () => {
    if (!selRapor.size) return
    setRaporBusy(true)
    try {
      const blob = await _raporFetch('pdf')
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const w = window.open(url, '_blank')
      if (w) setTimeout(() => { try { w.focus(); w.print() } catch (_) {} }, 1200)  // PDF yüklenince yazdır diyaloğu
      else alert('Açılır pencere engellendi — yazdırmak için açılan PDF sekmesinde Ctrl+P yapın')
    } catch (e) { alert('Yazdırılamadı: ' + (e?.message || '')) }
    finally { setRaporBusy(false) }
  }
  const pollRef = useRef(null)
  const scrollAnchor = useRef(null)  // {secId, offsetFromTop}
  const toggle = (key) => setCollapsed(p => ({ ...p, [key]: !p[key] }))
  const toggleSec = (key) => setCollapsedSecs(p => ({ ...p, [key]: !p[key] }))
  const toggleRow = (key) => setExpandedRows(p => ({ ...p, [key]: !p[key] }))
  const toggleInfo = (key) => setOpenInfo(p => ({ ...p, [key]: !p[key] }))

  // Bölüm butonuna basınca o başlığa smooth scroll (mail + klasik). Kapalıysa açar.
  const scrollToSec = (kw) => {
    const root = document.querySelector('.ar-mail-view') || document
    const els = root.querySelectorAll('[data-toggle], h2, h3')
    const low = kw.toLocaleLowerCase('tr-TR')
    const target = Array.from(els).find(el => (el.textContent || '').toLocaleLowerCase('tr-TR').includes(low))
    if (!target) return
    const id = target.getAttribute && target.getAttribute('data-toggle')
    if (id) { const d = document.getElementById(id); if (d && d.style.display === 'none') d.style.display = '' }
    // Sticky üst bar payı: scroll-margin-top ile (hangi scroll container olursa olsun çalışır)
    const bar = document.querySelector('.sticky.top-0')
    const offset = (bar ? bar.offsetHeight : 90) + 16
    setTimeout(() => {
      target.style.scrollMarginTop = offset + 'px'
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 60)  // kapalı bölüm açıldıysa layout otursun
  }

  // Mail görünümünde HTML içine tıklama: bölüm aç/kapat (data-toggle) + detay pop-up (data-clk)
  const onMailClick = (e) => {
    const tog = e.target.closest('[data-toggle]')
    if (tog) {
      const el = document.getElementById(tog.getAttribute('data-toggle'))
      if (el) el.style.display = (el.style.display === 'none') ? '' : 'none'
      return
    }
    const clk = e.target.closest('[data-clk]')
    if (clk) {
      const type = clk.getAttribute('data-clk')
      const value = clk.getAttribute('data-val')
      const ds = clk.getAttribute('data-ds')
      const sef = clk.getAttribute('data-seferler')   // Sipariş Düşen: bakiye + Seferler butonu
      const extra = type === 'grup' ? { ds_id: Number(ds) }
                  : sef ? { seferler_cari: sef }
                  : undefined
      setDetayModal({ type, value, extra })
    }
  }

  const changePeriod = (newPeriod) => {
    // Period degisimi oncesi: viewport'taki en ust section ve offset'ini tut
    const STICKY_OFFSET = 80
    const secs = document.querySelectorAll('[data-section-id]')
    for (const sec of secs) {
      const r = sec.getBoundingClientRect()
      if (r.bottom > STICKY_OFFSET) {
        scrollAnchor.current = { secId: sec.dataset.sectionId, offset: r.top - STICKY_OFFSET }
        break
      }
    }
    setPeriod(newPeriod)
  }

  useLayoutEffect(() => {
    if (!data || !scrollAnchor.current) return
    const { secId, offset } = scrollAnchor.current
    const target = document.querySelector(`[data-section-id="${secId}"]`)
    const scroller = document.querySelector('main.overflow-y-auto') || document.scrollingElement
    if (target && scroller) {
      const r = target.getBoundingClientRect()
      const sRect = scroller.getBoundingClientRect ? scroller.getBoundingClientRect() : { top: 0 }
      const desired = (scroller === document.scrollingElement ? 80 : sRect.top + 80) + offset
      const delta = r.top - desired
      scroller.scrollBy({ top: delta, left: 0, behavior: 'instant' })
    }
    scrollAnchor.current = null
  }, [data])

  const load = async (force = false) => {
    setLoading(true)
    try {
      const params = { period, arac_year: aracYear, arac_month: aracMonth }
      if (paraModu === 'tl') params.para = 'tl'
      if (force) params.refresh = true  // yenile tusu: cache bypass + arka plan yeniden hesapla
      const { data } = await api.get('/anlik-rapor', { params })
      setData(data)
      // Tum tablolari acik baslat (default_collapsed yok sayilir) — kullanici basmadan gormek istiyor
      setCollapsed({})
      // Force refresh sonrasi arka planda yeni hesaplama suruyorsa, bitene kadar poll et
      if (force && (data.refreshing || data.preparing)) startPoll(data.generated_at)
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  // Arka plan yeniden hesaplama bitene kadar her 12sn yokla; yeni cache gelince goster
  const startPoll = (prevGen) => {
    setRefreshing(true)
    if (pollRef.current) clearInterval(pollRef.current)
    let tries = 0
    pollRef.current = setInterval(async () => {
      tries++
      try {
        const { data } = await api.get('/anlik-rapor', { params: { period, arac_year: aracYear, arac_month: aracMonth, ...(paraModu === 'tl' ? { para: 'tl' } : {}) } })
        if (!data.refreshing && !data.preparing && data.generated_at !== prevGen) {
          setData(data); setCollapsed({})
          setRefreshing(false); clearInterval(pollRef.current); pollRef.current = null
        }
      } catch (e) { console.error(e) }
      if (tries >= 30) { setRefreshing(false); clearInterval(pollRef.current); pollRef.current = null }  // ~6dk guvenlik siniri
    }, 12000)
  }

  useEffect(() => {
    load()
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; setRefreshing(false) } }
  }, [period, aracYear, aracMonth, paraModu])

  // Mail görünümü HTML'i (birebir mail tasarımı, gauge'lar dahil)
  useEffect(() => {
    if (viewMode !== 'mail') return
    setHtmlLoading(true)
    api.get(`/anlik-rapor/web-html?period=${period}${paraModu === 'tl' ? '&para=tl' : ''}`)
      .then(({ data }) => setHtmlContent(data.html || ''))
      .catch(() => setHtmlContent("<div style='padding:20px;color:#dc2626'>Mail görünümü yüklenemedi</div>"))
      .finally(() => setHtmlLoading(false))
  }, [viewMode, period, refreshing, paraModu])

  return (
    <div className="p-3 md:p-5">
      <div className="sticky top-0 z-20 -mx-3 md:-mx-5 px-3 md:px-5 py-3 mb-4 bg-gray-50/95 dark:bg-gray-900/95 backdrop-blur border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-lg md:text-xl font-bold text-gray-800 dark:text-gray-100">Anlık Rapor</h1>
          {data && <p className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 truncate">{data.start_date} → {data.end_date} • {new Date(data.generated_at).toLocaleString('tr-TR')}
            {refreshing && <span className="text-primary-500 animate-pulse"> · yenileniyor… (~3 dk)</span>}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          {/* Bölüm hızlı geçiş butonları — basınca o bölüme scroll */}
          <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
            {[['⛽', 'Petrol'], ['🚚', 'Yurtiçi'], ['🌍', 'Yurtdışı'], ['🛒', 'Market'], ['🧇', 'Kumpir'], ['🛢️', 'Tank Doluluk']].map(([ic, kw]) => (
              <button key={kw} onClick={() => scrollToSec(kw)} title={`${kw} bölümüne git`}
                className="text-xs px-2.5 py-1.5 font-medium text-gray-600 dark:text-gray-300 hover:bg-primary-100 dark:hover:bg-gray-700 border-r border-gray-200 dark:border-gray-700 last:border-r-0 whitespace-nowrap">
                {ic} {kw.split(' ')[0]}
              </button>
            ))}
          </div>
          <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
            {[
              { k: 'yesterday', l: 'Dün' },
              { k: 'daily', l: 'Şimdi' },
              { k: 'weekly', l: 'Haftalık' },
              { k: 'monthly', l: 'Aylık' },
            ].map(p => (
              <button key={p.k} onClick={() => changePeriod(p.k)}
                className={`text-xs px-3 py-1.5 font-medium transition-colors ${period === p.k ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}>
                {p.l}
              </button>
            ))}
          </div>
          {/* qr22: para birimi modu — doviz tutarlarin ₺ karsiligi (her hareket kendi islem kuruyla) */}
          <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
            {[['', '₺/€ ayrı', 'Her tutar kendi para biriminde gösterilir (çevrim yapılmaz)'],
              ['tl', '₺ karşılığı', 'Döviz tutarlar kendi işlem kuruyla ₺ karşılığına çevrilir']].map(([k, l, ipucu]) => (
              <button key={k} onClick={() => setParaModu(k)} title={ipucu}
                className={`text-xs px-3 py-1.5 font-medium transition-colors ${paraModu === k ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}>
                {l}
              </button>
            ))}
          </div>
          <button onClick={() => setViewMode(m => m === 'mail' ? 'klasik' : 'mail')}
            title="Görünüm: Mail tasarımı / Klasik tablolar"
            className="text-xs px-3 py-1.5 rounded-lg font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">
            {viewMode === 'mail' ? '📋 Klasik' : '📧 Mail Görünümü'}
          </button>
          <button onClick={() => setRaporOpen(true)} title="Tablo seçip PDF/Excel indir"
            className="text-xs px-3 py-1.5 rounded-lg font-medium bg-primary-600 text-white hover:bg-primary-700">
            📥 Rapor Al
          </button>
          <button onClick={() => load(true)} disabled={loading || refreshing} title="Yenile (güncel veriyi yeniden hesapla)"
            className={`p-1.5 rounded-lg transition-colors ${(loading || refreshing) ? 'text-primary-400' : 'text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-gray-800'}`}>
            <FiRefreshCw size={16} className={(loading || refreshing) ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {viewMode === 'mail' && (
        <div>
          {htmlLoading && <div className="text-center py-10 text-gray-400 text-sm">Mail görünümü yükleniyor…</div>}
          <div className="ar-mail-view" onClick={onMailClick} dangerouslySetInnerHTML={{ __html: htmlContent }} />
        </div>
      )}

      {viewMode !== 'mail' && (<>
      {data?._conn_error && (
        <div className="mb-4 rounded-lg border border-red-300 dark:border-red-800 border-l-4 border-l-red-600 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-800 dark:text-red-300">
          ⚠️ <b>Sunucu Bağlantı Hatası</b> — Bazı veri kaynaklarına ulaşılamadı. İşaretli (🕒 eski veri) bölümler en son başarılı haliyle gösteriliyor
          {data._stale_since && <> · son güncelleme: <b>{new Date(data._stale_since).toLocaleString('tr-TR')}</b></>}. Bağlantı düzelince otomatik güncellenecek.
        </div>
      )}
      {loading && !data && (
        <div className="text-center py-20 text-gray-400">Yükleniyor...</div>
      )}

      {data?.sections?.map((sec, i) => {
        const Icon = ICONS[sec.icon] || FiAlertCircle
        const isTank = sec.title?.startsWith('Tank Doluluk')
        const secKey = `sec-${i}`
        const isSecCollapsed = !!collapsedSecs[secKey]
        const secId = getSecId(sec.title)
        return (
          <div key={i} data-section-id={secId} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 mb-4">
            <div className={`flex items-center gap-2 ${isSecCollapsed ? '' : 'mb-3 pb-2 border-b dark:border-gray-700'} -mx-1 px-1 py-1`}>
              <button onClick={() => toggleSec(secKey)}
                className="flex-1 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded text-left">
                {isSecCollapsed ? <FiChevronRight size={14} className="text-gray-400"/> : <FiChevronDown size={14} className="text-gray-400"/>}
                <Icon className="text-primary-600 dark:text-primary-400" size={18} />
                <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{sec.title}</h2>
                {sec._stale && (
                  <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded px-1.5 py-0.5 whitespace-nowrap">
                    🕒 eski veri{sec._stale_at ? ` · ${new Date(sec._stale_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}
                  </span>
                )}
                {sec.kpis && sec.kpis.length > 0 && isSecCollapsed && (
                  <span className="text-[10px] text-gray-400">{sec.kpis.length} KPI · {sec.tables?.length || 0} tablo</span>
                )}
              </button>
              {sec._render_extras?.arac_month_picker && !isSecCollapsed && (
                <AyPicker year={aracYear} month={aracMonth} onChange={(y, m) => { setAracYear(y); setAracMonth(m) }} />
              )}
              {!sec.error && !isSecCollapsed && (() => {
                const exp = exportSection(sec)
                return <ExportButtons rows={exp.rows} columns={exp.columns} filename={`${(sec.title || 'rapor').replace(/[^\w\s-]/g, '').substring(0, 40)}`} title={sec.title} label={sec.title} isMobile={isMobile} />
              })()}
            </div>

            {isSecCollapsed ? null : sec.error ? (
              <p className="text-xs text-red-500 italic">Veri çekilemedi: {sec.error}</p>
            ) : sec._render === 'yorum' ? (
              <YorumKartlari uyarilar={sec.uyarilar || []}/>
            ) : (
              <>
                {isTank && <TankCharts section={sec} />}
                {sec.kpis && sec.kpis.length > 0 && (
                  <div className={`grid grid-cols-2 gap-3 mb-4 ${
                    sec.kpis.length >= 6 ? 'md:grid-cols-6' :
                    sec.kpis.length === 5 ? 'md:grid-cols-5' :
                    sec.kpis.length === 4 ? 'md:grid-cols-4' :
                    sec.kpis.length === 3 ? 'md:grid-cols-3' :
                    sec.kpis.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-1'
                  }`}>
                    {sec.kpis.map((kpi, j) => (
                      <div key={j} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-100 dark:border-gray-700">
                        <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">{kpi.label}</div>
                        <div className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-1">
                          {formatCell(kpi.value, kpi.format, kpi.currency)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!isTank && sec.tables?.map((tbl, j) => {
                  const key = `${i}-${j}`
                  const isCollapsed = collapsed[key]
                  const showInfo = openInfo[key]
                  return (
                  <div key={j} className="mt-3">
                    <div className="flex items-center justify-between mb-1.5 px-1 gap-1">
                      <button onClick={() => toggle(key)}
                        className="flex-1 flex items-center gap-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 rounded py-0.5">
                        {isCollapsed ? <FiChevronRight size={11} /> : <FiChevronDown size={11} />}
                        <h3 className="text-xs font-medium text-gray-600 dark:text-gray-300">{tbl.title} <span className="text-gray-400 font-normal">({tbl.rows.length})</span></h3>
                      </button>
                      <ExportButtons rows={tbl.rows} columns={tbl.columns} filename={`${(tbl.title || 'tablo').replace(/[^\w\s-]/g, '').substring(0, 40)}`} title={tbl.title} label={tbl.title} isMobile={isMobile}/>
                      {tbl.info && (
                        <button onClick={() => toggleInfo(key)}
                          className={`p-1 rounded transition-colors ${showInfo ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30'}`}
                          title="Bilgi">
                          <FiInfo size={12}/>
                        </button>
                      )}
                    </div>
                    {tbl.info && showInfo && (
                      <div className="mb-2 bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-400 dark:border-blue-500 px-3 py-2 text-[11px] text-gray-700 dark:text-gray-300 whitespace-pre-line rounded-r">
                        {tbl.info}
                      </div>
                    )}
                    {isCollapsed ? null : (
                    tbl.rows.length === 0 ? (
                      <p className="text-xs text-gray-400 italic pl-5">Veri yok</p>
                    ) : (
                      <div className="overflow-auto max-h-[70vh] border border-gray-100 dark:border-gray-700 rounded">
                        <table className="w-full text-xs anlik-table">
                          <thead className="sticky top-0 z-20">
                            <tr className="bg-gray-50 dark:bg-gray-700">
                              {tbl.rows.some(r => r._details) && (
                                <th className="px-1 py-1.5 w-6 bg-gray-50 dark:bg-gray-700"></th>
                              )}
                              {tbl.columns.map((col, ci) => (
                                <th key={col.key} className={`px-2 py-1.5 text-left font-medium text-gray-600 dark:text-gray-300 ${col.format ? 'text-right' : ''} bg-gray-50 dark:bg-gray-700 ${ci === 0 ? 'sticky left-0 z-30 shadow-[2px_0_3px_-1px_rgba(0,0,0,0.06)]' : ''}`}>
                                  {col.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {tbl.rows.map((row, k) => {
                              const hasSubrows = (tbl.rows || []).some(r => r._subrow)
                              const rc = row._row_color
                              const isTotal = row._total
                              const rowCls = isTotal ? 'bg-gray-100 dark:bg-gray-700 font-semibold border-t-2 border-gray-300 dark:border-gray-600'
                                           : rc === 'red'    ? 'bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-900/60'
                                           : rc === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/40 hover:bg-yellow-200 dark:hover:bg-yellow-900/60'
                                           : rc === 'blue'   ? 'bg-sky-100 dark:bg-sky-900/40 hover:bg-sky-200 dark:hover:bg-sky-900/60'
                                           : rc === 'green'  ? 'bg-green-100 dark:bg-green-900/40 hover:bg-green-200 dark:hover:bg-green-900/60'
                                           : rc === 'orange' ? 'bg-orange-100 dark:bg-orange-900/40 hover:bg-orange-200 dark:hover:bg-orange-900/60'
                                           : 'hover:bg-blue-50/30 dark:hover:bg-blue-900/20'
                              // Ilk sutun sticky icin row-spec bg
                              const firstColBg = isTotal ? 'bg-gray-100 dark:bg-gray-700'
                                : rc === 'red'    ? 'bg-red-100 dark:bg-red-900/40'
                                : rc === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/40'
                                : rc === 'blue'   ? 'bg-sky-100 dark:bg-sky-900/40'
                                : rc === 'green'  ? 'bg-green-100 dark:bg-green-900/40'
                                : rc === 'orange' ? 'bg-orange-100 dark:bg-orange-900/40'
                                : 'bg-white dark:bg-gray-800'
                              const hasDetails = Array.isArray(row._details)
                              const detailCols = tbl.detail_columns || SEFER_DETAY_COLS
                              const detailTitle = tbl.detail_title || 'Sefer Detayları'
                              const rowKey = `${key}-${row.id || row.grup || k}-${k}`
                              const isOpen = !!expandedRows[rowKey]
                              const colSpan = tbl.columns.length + (hasDetails ? 1 : 0)
                              // Alt satırlı tabloda her yeni sefer (ana satır) üstten KALIN çizgiyle ayrılsın
                              const sepCls = (hasSubrows && !row._subrow && !isTotal && k > 0)
                                ? 'border-t-2 border-gray-400 dark:border-gray-500' : ''
                              return (
                              <FragmentRow key={k} hasDetails={hasDetails}>
                                <tr className={`border-b border-gray-50 dark:border-gray-700 ${sepCls} ${rowCls}`}>
                                  {hasDetails && (
                                    <td className={`px-1 py-1 text-center ${firstColBg}`}>
                                      {row._details.length > 0 ? (
                                        <button onClick={() => toggleRow(rowKey)}
                                          className="text-gray-500 hover:text-primary-600 transition-colors"
                                          title={`${row._details.length} detay`}>
                                          {isOpen ? <FiChevronDown size={12}/> : <FiChevronRight size={12}/>}
                                        </button>
                                      ) : <span className="text-gray-300 text-[10px]">—</span>}
                                    </td>
                                  )}
                                  {tbl.columns.map((col, ci) => {
                                    const tetik = isTotal ? null : _detayTetik(col.key, row[col.key], row)
                                    return (
                                    <td key={col.key}
                                      onClick={tetik ? (e) => { e.stopPropagation(); setDetayModal(tetik) } : undefined}
                                      className={`px-2 py-1 text-gray-700 dark:text-gray-300 ${col.format ? 'text-right tabular-nums' : ''} ${tetik ? 'cursor-pointer text-blue-600 dark:text-blue-400 hover:underline hover:bg-blue-50/50 dark:hover:bg-blue-900/30' : ''} ${ci === 0 ? `sticky left-0 z-[1] ${firstColBg} shadow-[2px_0_3px_-1px_rgba(0,0,0,0.06)]` : ''}`}>
                                      {formatCell(row[col.key], col.format)}
                                    </td>
                                  )})}
                                </tr>
                                {hasDetails && isOpen && row._details.length > 0 && (
                                  <tr className="bg-gray-50/80 dark:bg-gray-900/40">
                                    <td colSpan={colSpan} className="px-3 py-2 border-b dark:border-gray-700">
                                      <DetayTable details={row._details} columns={detailCols} title={detailTitle} />
                                    </td>
                                  </tr>
                                )}
                              </FragmentRow>
                            )})}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                )})}
              </>
            )}
          </div>
        )
      })}
      </>)}
      {detayModal && (
        <LojDetayModal type={detayModal.type} value={detayModal.value}
          extra={detayModal.extra ? { ...detayModal.extra, period } : undefined}
          onClose={() => setDetayModal(null)} />
      )}
      {raporOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={() => setRaporOpen(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-slate-700 dark:bg-slate-900 text-white px-4 py-2.5 flex items-center gap-2 flex-shrink-0">
              <span className="text-sm font-semibold">📥 Rapor Al — İndirilecek tabloları seç</span>
              <button onClick={() => setRaporOpen(false)} className="ml-auto text-white/70 hover:text-white text-lg leading-none">×</button>
            </div>
            <div className="overflow-auto p-3 flex-1 text-sm">
              {(() => {
                const allKeys = []
                ;(data?.sections || []).forEach(s => (s.tables || []).forEach(t => { if ((t.rows || []).length) allKeys.push(`${s.title}|||${t.title}`) }))
                return (
                  <>
                    <div className="flex gap-2 mb-2 pb-2 border-b dark:border-gray-700">
                      <button onClick={() => setSelRapor(new Set(allKeys))} className="text-xs px-2 py-1 rounded bg-primary-50 dark:bg-gray-700 text-primary-700 dark:text-gray-200 hover:bg-primary-100">Tümünü Seç ({allKeys.length})</button>
                      <button onClick={() => setSelRapor(new Set())} className="text-xs px-2 py-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700">Temizle</button>
                      <span className="ml-auto text-xs text-gray-400 self-center">{selRapor.size} seçili</span>
                    </div>
                    {(data?.sections || []).map((s, si) => {
                      const tbls = (s.tables || []).filter(t => (t.rows || []).length)
                      if (!tbls.length) return null
                      const secKeys = tbls.map(t => `${s.title}|||${t.title}`)
                      const allSel = secKeys.every(k => selRapor.has(k))
                      return (
                        <div key={si} className="mb-2">
                          <label className="flex items-center gap-2 font-semibold text-gray-700 dark:text-gray-200 cursor-pointer">
                            <input type="checkbox" checked={allSel} onChange={() => setSelRapor(prev => {
                              const n = new Set(prev); if (allSel) secKeys.forEach(k => n.delete(k)); else secKeys.forEach(k => n.add(k)); return n
                            })} />
                            {s.title}
                          </label>
                          <div className="pl-6">
                            {tbls.map((t, ti) => {
                              const key = `${s.title}|||${t.title}`
                              return (
                                <label key={ti} className="flex items-center gap-2 py-0.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                                  <input type="checkbox" checked={selRapor.has(key)} onChange={() => setSelRapor(prev => {
                                    const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n
                                  })} />
                                  {t.title} <span className="text-gray-400">({t.rows.length})</span>
                                </label>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}
                  </>
                )
              })()}
            </div>
            <div className="border-t dark:border-gray-700 p-3 flex gap-2 justify-end flex-shrink-0">
              <button onClick={() => raporIndir('xlsx')} disabled={raporBusy || !selRapor.size}
                className="text-sm px-3 py-1.5 rounded-lg font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-40">
                {raporBusy ? '...' : '📊 Excel İndir'}
              </button>
              <button onClick={() => raporIndir('pdf')} disabled={raporBusy || !selRapor.size}
                className="text-sm px-3 py-1.5 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-40">
                {raporBusy ? '...' : '📄 PDF İndir'}
              </button>
              <button onClick={raporYazdir} disabled={raporBusy || !selRapor.size}
                className="text-sm px-3 py-1.5 rounded-lg font-medium bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-40">
                {raporBusy ? '...' : '🖨️ Yazdır'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
